<script>
// GPS user data heatmap. Ported from the standalone prototype
// (gps-userdata-map.html). The Google Maps API key and backend base URL now
// come from the build config (process.env) instead of being hardcoded.
//
// deck.gl provides a real GPU density heatmap (the native Google HeatmapLayer
// was removed from the Maps JS API in v3.65). It is not an npm dependency, so
// we load it from the CDN on demand, matching the prototype.
const DECK_SRC = 'https://unpkg.com/deck.gl@9.0.33/dist.min.js'

// Default view: centred over Hungary, zoomed out to the whole country. Once
// data loads, fitToPoints() zooms in to the actual points.
const DEFAULT_CENTER = { lat: 47.162494, lng: 19.503304 }
const DEFAULT_ZOOM = 7

// Fixed page size for the progressive loader — the whole result set is always
// pulled, one bounded batch at a time.
const BATCH_SIZE = 5000

function loadDeck()
{
    if (window.deck) return Promise.resolve()
    if (window.__deckPromise) return window.__deckPromise
    window.__deckPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = DECK_SRC
        s.onload = resolve
        s.onerror = () => reject(new Error('Failed to load deck.gl'))
        document.head.appendChild(s)
    })
    return window.__deckPromise
}

export default
{
    name: 'gps-userdata-map',

    data()
    {
        return {
            apiBase: `${process.env.API_URL}/v1/gps-userdata`,
            defaultCenter: DEFAULT_CENTER,
            defaultZoom: DEFAULT_ZOOM,

            devices: [],
            users: [],

            device: '',
            user: '',
            from: '',
            to: '',

            showHeatmap: true,
            showPaths: false,
            radius: 64,

            status: 'Ready.',

            // map / rendering state
            google: null,
            map: null,
            mapReady: false,
            deckOverlay: null,
            polylines: [],
            lastPoints: [],
            loadToken: 0, // bumped on each Load/Clear to cancel in-flight paging
        }
    },

    computed:
    {
        // Searchable-select options: carry the id + a human label to display.
        deviceOptions()
        {
            return this.devices.map(d => ({
                id: d.device_id,
                label: `${d.device_id} (${d.points})`,
            }))
        },

        userOptions()
        {
            return this.users.map(u => ({
                id: u.user_id,
                label: `${u.name || ('User #' + u.user_id)} (${u.points})`,
            }))
        },
    },

    watch:
    {
        showHeatmap() { this.render() },
        showPaths() { this.render() },
        radius() { this.renderHeat() },
    },

    methods:
    {
        setStatus(msg) { this.status = msg },

        onDeviceSelect(opt) { this.device = opt ? opt.id : '' },
        onUserSelect(opt) { this.user = opt ? opt.id : '' },

        // Google Maps is loaded lazily by vue2-google-maps once <gmap-map>
        // mounts. Once its map object is ready, attach the deck.gl overlay and
        // (re)draw anything that arrived while the map was still loading.
        async initMap()
        {
            try {
                await loadDeck()
                const map = await this.$refs.gmap.$mapCreated
                this.google = window.google
                this.map = map
                this.deckOverlay = new window.deck.GoogleMapsOverlay({ layers: [] })
                this.deckOverlay.setMap(map)
                this.mapReady = true

                if (this.lastPoints.length) {
                    this.buildLayers(this.lastPoints)
                    this.render()
                    this.fitToPoints(this.lastPoints)
                }
            } catch (e) {
                this.setStatus('Map failed to initialise: ' + e.message)
            }
        },

        async loadFilters()
        {
            try {
                const res = await fetch(this.apiBase + '/filters')
                const data = await res.json()
                this.devices = data.devices || []
                this.users = data.users || []
                this.setStatus(`${data.total} total points across ${this.devices.length} device(s).`)
            } catch (e) {
                this.setStatus('Failed to load filters: ' + e.message)
            }
        },

        // Progressively pull the whole result set in bounded batches, updating
        // the heatmap after each one, so neither the client nor the server has
        // to handle the whole dataset in a single request.
        async loadData()
        {
            const token = ++this.loadToken // invalidates any previous in-flight run

            const base = new URLSearchParams()
            if (this.device) base.set('device_id', this.device)
            if (this.user) base.set('user_id', this.user)
            if (this.from) base.set('from', this.from + ' 00:00:00')
            if (this.to) base.set('to', this.to + ' 23:59:59')
            base.set('limit', BATCH_SIZE)

            this.lastPoints = []
            let afterId = null
            let firstFit = false

            try {
                while (true) {
                    const params = new URLSearchParams(base)
                    if (afterId !== null) params.set('after_id', afterId)

                    this.setStatus(`Loading… ${this.lastPoints.length} point(s) so far`)
                    const res = await fetch(this.apiBase + '?' + params.toString())
                    if (!res.ok) throw new Error('HTTP ' + res.status)
                    const data = await res.json()

                    if (token !== this.loadToken) return // a newer load/clear superseded us

                    this.lastPoints = this.lastPoints.concat(data.points)
                    this.renderHeat() // stream the heatmap in as batches arrive

                    if (!firstFit && this.lastPoints.length) {
                        this.fitToPoints(this.lastPoints)
                        firstFit = true
                    }

                    afterId = data.next_after_id
                    if (!data.has_more || afterId === null) break
                }

                // Paths are (re)built once at the end — cheaper than per batch.
                this.buildLayers(this.lastPoints)
                this.render()

                if (!this.lastPoints.length) {
                    this.setStatus('No points found for these filters.')
                } else {
                    this.setStatus(`Showing ${this.lastPoints.length} point(s).`)
                }
            } catch (e) {
                if (token === this.loadToken) this.setStatus('Failed to load data: ' + e.message)
            }
        },

        buildLayers(points)
        {
            if (!this.mapReady) return // will be rebuilt once initMap runs
            this.removeOverlays()

            // Paths grouped by device, ordered by time (points come pre-sorted by created_at)
            const byDevice = {}
            points.forEach(p => {
                (byDevice[p.device_id] = byDevice[p.device_id] || []).push({ lat: +p.latitude, lng: +p.longitude })
            })
            const colors = ['#007aff', '#ff3b30', '#34c759', '#ff9500', '#af52de', '#00c7be', '#ff2d55']
            let i = 0
            this.polylines = Object.values(byDevice).map(path => {
                const color = colors[i++ % colors.length]
                return new this.google.maps.Polyline({
                    path,
                    strokeColor: color,
                    strokeOpacity: 0.7,
                    strokeWeight: 3,
                    // Repeating arrowheads point from each earlier fix to the
                    // next, i.e. the direction the device actually travelled.
                    icons: [{
                        icon: {
                            path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                            scale: 2.5,
                            strokeColor: color,
                            strokeWeight: 2,
                            fillColor: color,
                            fillOpacity: 1,
                        },
                        offset: '0',
                        repeat: '90px',
                    }],
                })
            })
        },

        render()
        {
            if (!this.mapReady) return
            this.polylines.forEach(l => l.setMap(this.showPaths ? this.map : null))
            this.renderHeat()
        },

        // GPU density heatmap: colour intensity reflects how many points were
        // collected in an area, fading out granularly as density drops.
        renderHeat()
        {
            if (!this.mapReady || !this.deckOverlay) return
            const layers = []
            if (this.showHeatmap && this.lastPoints.length) {
                layers.push(new window.deck.HeatmapLayer({
                    id: 'gps-heat',
                    data: this.lastPoints,
                    getPosition: d => [+d.longitude, +d.latitude],
                    getWeight: 1,
                    aggregation: 'SUM',
                    radiusPixels: parseInt(this.radius, 10) || 40,
                    intensity: 1,
                    threshold: 0.03,
                }))
            }
            this.deckOverlay.setProps({ layers })
        },

        removeOverlays()
        {
            this.polylines.forEach(l => l.setMap(null))
            this.polylines = []
        },

        clearMap()
        {
            this.loadToken++ // cancel any in-flight progressive load
            this.removeOverlays()
            this.lastPoints = []
            this.renderHeat()
            this.setStatus('Cleared.')
        },

        fitToPoints(points)
        {
            if (!this.mapReady) return
            const bounds = new this.google.maps.LatLngBounds()
            points.forEach(p => bounds.extend({ lat: +p.latitude, lng: +p.longitude }))
            this.map.fitBounds(bounds)
        },
    },

    mounted()
    {
        this.initMap()
        this.loadFilters()
        this.loadData()
    },

    beforeDestroy()
    {
        this.loadToken++ // cancel any in-flight progressive load
        this.removeOverlays()
        if (this.deckOverlay) this.deckOverlay.setMap(null)
    },
}
</script>

<template>
<div class="container-fluid">
    <div class="panel panel-primary">
        <div class="panel-heading">
            <div class="row">
                <div class="col-sm-10">
                    <h4>{{ $t('MENU.GPSMAP') }}</h4>
                </div>
            </div>
        </div>
        <div class="panel-body">
            <div class="row gps-map-controls">
                <div class="col-md-3 form-group">
                    <label>{{ $t('GPSMAP.DEVICE') }}</label>
                    <v-select :options="deviceOptions" label="label" :placeholder="$t('GPSMAP.ALL_DEVICES')" @input="onDeviceSelect"></v-select>
                </div>
                <div class="col-md-3 form-group">
                    <label>{{ $t('GPSMAP.USER') }}</label>
                    <v-select :options="userOptions" label="label" :placeholder="$t('GPSMAP.ALL_USERS')" @input="onUserSelect"></v-select>
                </div>
                <div class="col-md-2 form-group">
                    <label>{{ $t('GPSMAP.FROM') }}</label>
                    <input type="date" class="form-control" v-model="from">
                </div>
                <div class="col-md-2 form-group">
                    <label>{{ $t('GPSMAP.TO') }}</label>
                    <input type="date" class="form-control" v-model="to">
                </div>
                <div class="col-md-2 form-group gps-map-buttons">
                    <label>&nbsp;</label>
                    <button class="btn btn-primary btn-block" @click="loadData">{{ $t('GPSMAP.LOAD') }}</button>
                </div>
            </div>
            <div class="row gps-map-options">
                <div class="col-md-3">
                    <div class="checkbox">
                        <label><input type="checkbox" v-model="showHeatmap"> {{ $t('GPSMAP.HEATMAP') }}</label>
                    </div>
                </div>
                <div class="col-md-5 gps-map-radius">
                    <label>{{ $t('GPSMAP.HEAT_RADIUS') }}: {{ radius }} px</label>
                    <input type="range" v-model="radius" min="5" max="120">
                </div>
                <div class="col-md-2">
                    <div class="checkbox">
                        <label><input type="checkbox" v-model="showPaths"> {{ $t('GPSMAP.PATHS') }}</label>
                    </div>
                </div>
            </div>
            <gmap-map
                ref="gmap"
                :center="defaultCenter"
                :zoom="defaultZoom"
                map-type-id="roadmap"
                style="width: 100%; height: 78vh; border-radius: 8px;"
            >
            </gmap-map>
            <div class="gps-map-status">{{ status }}</div>
        </div>
    </div>
</div>
</template>

<style lang="scss">
.gps-map-options {
    margin-bottom: 1em;
    align-items: center;
}

.gps-map-radius {
    label {
        margin-bottom: 0;
    }

    input[type="range"] {
        margin-top: 0;
        width: 100%;
    }
}

.gps-map-status {
    margin-top: 1em;
    font-size: 0.85rem;
    color: #666;
    padding: 8px;
    background: #f0f0f3;
    border-radius: 6px;
    min-height: 20px;
}
</style>



// WEBPACK FOOTER //
// src/pages/GpsUserDataMap.vue